import { Component } from 'preact';
import { getLocalApiOrigin } from '../utils/localApi';
import { UI } from '../utils/arena/uiSounds';
import { CloseButton } from './ui/navButtons';
import { ScrollArea } from './ui/scroll-area';
import { getPref, setPref, removePref } from '../utils/persistentPrefs';
import { getCosmeticsCached, peekCosmeticsCached, awaitProtectedRegistry, peekProtectedBlobUrl, protectedMatVariantUrl } from '../utils/arena/cosmeticsCache';
import { isWeb } from '../utils/platform';
import { k, t } from '../localization/t';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  DIALOG_STYLE,
} from '../lib/medievalTheme';

// Light / dark grid mode colours. Off-white + off-black instead of
// pure #fff / #000 so the pencil-sketch grid reads as ink-on-paper
// rather than a crisp digital line. Warm tones tie the grid back to
// the medieval / parchment aesthetic of the rest of the UI.
const GRID_LIGHT = '#f0e8d4';
const GRID_DARK  = '#1c1814';

// Luma check used by the dark-grid toggle to decide whether a
// theme's default uiColor counts as "dark mode" when the player
// hasn't set an explicit override. Threshold at 128 = mid-grey.
function isHexDark(hex) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(hex || '').trim());
  if (!m) return false;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) < 128;
}

// Playmat themes come in two flavours:
//
// 1. **Layered (bundled)**. Separate art background + procedural UI overlay.
//    Art lives at playmats/art-only/{artFile} inside the Electron bundle.
//
// 2. **Layered (protected)**. Same layered structure, but the art
//    layer is licensed material that does NOT ship in the bundle.
//    The art URL points at the local /game-assets/protected-playmat/
//    endpoint, which fetches from Vercel Blob CDN, watermarks per
//    user, encrypts the cache, and serves decrypted bytes back.
//
// Paid themes set `cosmeticId` to the entitlement id granted by the
// matching pack in the cosmetics store. Vanilla has no cosmeticId
// because it ships free with the game.
//
// `uiInversionRegions` (optional). Array of rectangles in normalized
// 0..1 UV coords where the WHITE UI overlay should be flipped to BLACK
// at render time (specifically: every pixel's RGB is inverted, alpha
// preserved). Use it to keep grid lines / row letters / column numbers
// readable over light areas of the artwork without having to re-export
// a second dark-on-light UI overlay from Figma.
//
//   Format: `{ x, y, width, height }` per rectangle
//   - Origin (0, 0) = top-left of the mat as the artwork is laid out
//   - x + width and y + height should both stay within [0, 1]
//   - Multiple rectangles supported (up to 8); they all invert
//   - Outside any rectangle: UI overlay renders normally (white)
//   - `[{ x: 0, y: 0, width: 1, height: 1 }]` = invert the whole UI
//
// Backwards compatible: omit the field and nothing changes. Every
// pre-existing theme keeps rendering exactly as before.

const PLAYMAT_THEMES = [
  {
    id: 'valkenhall-vanilla',
    name: 'Valkenhall Marble',
    preview: 'art-only/vanilla-valkenhall-playmat.webp',
    layered: true,
    artFile: 'art-only/vanilla-valkenhall-playmat.webp',
    uiColor: GRID_LIGHT,
    // The vanilla art already has busy decorative borders, so dark
    // mode is locked off and the faded toggle is force-on. Keeps
    // the procedural UI legible without competing visually with the
    // painted background. `textureOverlay` paints a tiled paper-grain
    // over the whole mat surface so the otherwise-flat central play
    // area picks up the same hand-made feel as the borders.
    lightOnly: true,
    fadedLocked: true,
    textureOverlay: { url: '/tex-noise.webp', alpha: 0.18 },
  },
  {
    id: 'kraay-paint',
    name: "Journey's End",
    preview: 'art-only/peterkraayvanger-to-paint-69231.webp',
    layered: true,
    artFile: 'art-only/peterkraayvanger-to-paint-69231.webp',
    cosmeticId: 'playmat-kraay-paint',
    uiColor: GRID_LIGHT,
  },
  {
    id: 'courbet-grotto',
    name: 'Courbet Grotto',
    preview: 'art-only/gustave-courbet.webp',
    layered: true,
    artFile: 'art-only/gustave-courbet.webp',
    cosmeticId: 'playmat-courbet-grotto',
    uiColor: GRID_LIGHT,
  },

  // Collector Arthouse. Severine. Protected layered themes. The art
  // never ships in the bundle; the local server pulls it from Vercel
  // Blob on demand with a per-user watermark and encrypts the cache.
  // The artwork is light in the central play area where cards sit,
  // so the UI grid recolors to black via `uiColor` to keep grid
  // lines + numbers legible against the painted background.
  { id: 'bower-of-bliss',    name: 'Bower of Bliss',    layered: true, protected: true, cosmeticId: 'playmat-bower-of-bliss',    uiColor: GRID_DARK },
  { id: 'kythera-mechanism', name: 'Kythera Mechanism', layered: true, protected: true, cosmeticId: 'playmat-kythera-mechanism', uiColor: GRID_DARK },
  { id: 'element-avatars',   name: 'Elemental Avatars', layered: true, protected: true, cosmeticId: 'playmat-element-avatars',   uiColor: GRID_DARK },
  { id: 'sorcerers-stone',    name: "Philosopher's Stone (Arthouse Ed.)", layered: true, protected: true, cosmeticId: 'playmat-sorcerers-stone',    uiColor: GRID_DARK },
  { id: 'philosophers-stone', name: "Philosopher's Stone",                 layered: true, protected: true, cosmeticId: 'playmat-philosophers-stone', uiColor: GRID_DARK },
];

// URL for a protected (Vercel-Blob-backed, per-user-watermarked) mat,
// served by the local Electron server. The endpoint is keyed by the
// cosmetic entitlement id and resolves the actual Blob URL from the
// in-memory registry the renderer pushed at sign-in.
function protectedPlaymatUrl(cosmeticId) {
  // Web build: no local Electron server. The art loads from the CDN's
  // pre-baked 3500px `full/` variant, derived from the blobUrl the
  // cosmetics catalog ships per protected pack (null until the catalog
  // cache is primed; callers fall back to vanilla until then). The
  // blobUrl master itself is 68 MP and never rendered directly.
  if (isWeb()) return protectedMatVariantUrl(peekProtectedBlobUrl(cosmeticId), 'full');
  return `${getLocalApiOrigin()}/game-assets/protected-playmat/${encodeURIComponent(cosmeticId)}`;
}

// Gold seals baked into licensed mats' corners by the UI overlay
// generator: the house seal in P1's bottom-left, the artist seal in
// P2's. All current protected mats are Collector Arthouse / Séverine
// Pineaux; key these off catalog fields if more houses are added.
const PLAYMAT_SEAL_BASE = `${getLocalApiOrigin()}/game-assets/playmats/seals`;
const COLLECTOR_ARTHOUSE_SEAL_URL = `${PLAYMAT_SEAL_BASE}/collector-arthouse.webp`;
const ARTIST_SEAL_URL = `${PLAYMAT_SEAL_BASE}/severine-pineaux.webp`;

function isThemeOwned(theme, ownedCosmetics) {
  if (!theme?.cosmeticId) return true;
  return Array.isArray(ownedCosmetics) && ownedCosmetics.includes(theme.cosmeticId);
}

const LINE_STYLES = [
  { id: 'dashed', label: k('Dashed') },
  { id: 'dotted', label: k('Dotted') },
];

// Module-level preview blob URL cache. Keeps decoded thumbnails alive
// across PlaymatPicker mount/unmount cycles so the second+ time the
// player opens the editor, dropdown thumbs are instant (no fetch, no
// decode, no object-URL recreation). Keyed by theme.id; values are
// blob: URLs from URL.createObjectURL(). NEVER revoked. They're a
// few KB each, total max ~250 KB even with every cosmetic owned.
const previewBlobUrlCache = new Map();
const inflightBlobLoads = new Map();

/**
 * Kick off background loading of every playmat preview thumbnail so
 * the picker dropdown opens instantly the first time the player
 * uses it. Idempotent: a second call is a no-op if the cache is
 * already populated, and overlapping calls share the same fetch via
 * `inflightBlobLoads`. Returns a promise that resolves once every
 * preview is either cached or has failed.
 *
 * Call this from app startup (after sign-in) so the small ~30 KB
 * blobs are ready before the player navigates anywhere near the
 * playmat editor or the cosmetics store.
 */
export async function prewarmPlaymatPreviews() {
  // Protected preview/full fetches 404 with `not-registered` until the
  // registry PUT reaches the local server, so wait for it before
  // issuing any. Resolves immediately if the caller already primed the
  // registry (or there's nothing to push).
  try { await awaitProtectedRegistry(); } catch {}

  // Small ~30 KB thumbnails for every mat. These back the store grid
  // and the picker dropdown. Warm them SEQUENTIALLY, not in parallel:
  // a cold protected preview makes the main process decode a multi-MP
  // master, and firing four at once pegs every CPU core, which starves
  // the renderer process (the UI then crawls. Hover/click take
  // seconds). One at a time keeps a core free for the renderer.
  for (const t of PLAYMAT_THEMES) {
    await ensurePreviewBlob(t);
  }

  // Full art for the protected mats the player OWNS, so entering a
  // match with one selected doesn't pay the fetch + watermark cost at
  // table-load time. Bundled mats already ship their full art in the
  // app bundle, so they need no warming. Sequential for the same
  // CPU-contention reason as the previews above.
  try {
    const data = await getCosmeticsCached();
    const owned = new Set(data?.owned || []);
    for (const t of PLAYMAT_THEMES) {
      if (t.protected && t.cosmeticId && owned.has(t.cosmeticId)) {
        await warmFullProtectedMat(t);
      }
    }
  } catch {}
}

// Module-level guards so overlapping prewarm calls (ArenaHub +
// GameBoard both fire it) share a single in-flight fetch per mat and
// never re-warm one already on disk this session.
const warmedFullMats = new Set();
const inflightFullWarms = new Map();

async function warmFullProtectedMat(theme) {
  if (!theme?.protected || !theme.cosmeticId) return;
  if (warmedFullMats.has(theme.id)) return;
  if (inflightFullWarms.has(theme.id)) return inflightFullWarms.get(theme.id);
  const url = protectedPlaymatUrl(theme.cosmeticId);
  if (!url) return;
  const p = (async () => {
    try {
      const res = await fetch(url);
      if (res.ok) {
        // Drain the body so the localhost server finishes writing its
        // encrypted disk cache and the renderer HTTP cache holds the
        // decoded bytes for the eventual table render.
        await res.arrayBuffer();
        warmedFullMats.add(theme.id);
      }
    } catch {
      // Best-effort: the table render fetches on demand if this fails.
    } finally {
      inflightFullWarms.delete(theme.id);
    }
  })();
  inflightFullWarms.set(theme.id, p);
  return p;
}

async function ensurePreviewBlob(theme) {
  if (previewBlobUrlCache.has(theme.id)) return previewBlobUrlCache.get(theme.id);
  if (inflightBlobLoads.has(theme.id)) return inflightBlobLoads.get(theme.id);
  // Web resolves protected art from the cosmetics catalog's blobUrl,
  // so make sure the catalog cache is primed before building the URL.
  if (isWeb() && theme.protected) {
    try { await getCosmeticsCached(); } catch {}
  }
  const url = getPreviewUrl(theme);
  if (!url) return null;
  const p = (async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const blob = await res.blob();
      const obj = URL.createObjectURL(blob);
      previewBlobUrlCache.set(theme.id, obj);
      return obj;
    } catch {
      return null;
    } finally {
      inflightBlobLoads.delete(theme.id);
    }
  })();
  inflightBlobLoads.set(theme.id, p);
  return p;
}

const STORAGE_KEY = 'valkenhall-playmat';

// First-run playmat editor defaults. Applied to fresh installs (no
// saved prefs) and to legacy string-format migrations. Existing users
// with a structured saved object keep their own choices. The per-key
// fallbacks below only fill gaps, they don't override explicit picks.
const DEFAULT_PLAYMAT_SETTINGS = {
  theme: 'valkenhall-vanilla',
  lineStyle: 'dashed',
  fullyNumbered: false,
  faded: true,
  gridDark: false,
  thickLines: true,
  largeNumbers: false,
  showLifeTracker: true,
  showManaTracker: true,
  showAffinityTracker: true,
};

function loadSettings() {
  const saved = getPref(STORAGE_KEY, null);
  // Migration: old format was just a string like "dashed"
  if (typeof saved === 'string') {
    removePref(STORAGE_KEY);
    return { ...DEFAULT_PLAYMAT_SETTINGS };
  }
  // Fresh install. No saved prefs yet. Seed the requested defaults.
  if (saved == null) {
    return { ...DEFAULT_PLAYMAT_SETTINGS };
  }
  const s = saved;
  return {
    theme: s.theme || 'valkenhall-vanilla',
    lineStyle: s.lineStyle || 'dashed',
    fullyNumbered: s.fullyNumbered || false,
    faded: s.faded || false,
    // Per-player override for grid colour: null = use the theme's
    // default `uiColor`; true = force dark (black) grid + numbers +
    // inverted rune ornament; false = force light (white). Stored
    // as null on a fresh install so the per-theme default applies
    // until the player explicitly toggles.
    gridDark: typeof s.gridDark === 'boolean' ? s.gridDark : null,
    thickLines: s.thickLines === true,
    largeNumbers: s.largeNumbers === true,
    // Per-player tracker toggles (new in the code-driven UI overlay
    // era). Default everything on. These only ship visibly once
    // the picker UI exposes the toggles (task #13).
    showLifeTracker: s.showLifeTracker !== false,
    showManaTracker: s.showManaTracker !== false,
    showAffinityTracker: s.showAffinityTracker !== false,
  };
}

function saveSettings(settings) {
  setPref(STORAGE_KEY, settings);
}

function buildFilename(settings) {
  let name = settings.lineStyle;
  if (settings.fullyNumbered) name += '-fully-numbered';
  if (settings.faded) name += '-faded';
  // Check if this exact file exists in our known set
  const known = new Set([
    'dashed', 'dashed-faded', 'dashed-fully-numbered', 'dashed-fully-numbered-faded',
    'dotted-faded', 'dotted-fully-numbered', 'dotted-fully-numbered-faded',
  ]);
  if (!known.has(name)) {
    // Fallback: add fully-numbered if missing
    const withNum = settings.lineStyle + '-fully-numbered' + (settings.faded ? '-faded' : '');
    if (known.has(withNum)) return `${withNum}.webp`;
    return `${settings.lineStyle}.webp`;
  }
  return `${name}.webp`;
}

export function getSavedPlaymat() {
  return loadSettings();
}

// Persist the player's active playmat theme, keeping all other grid/UI
// settings as-is. Used by the onboarding starter-playmat pick.
export function setSavedPlaymatTheme(themeId) {
  const settings = loadSettings();
  saveSettings({ ...settings, theme: themeId });
}

// Read the UI-inversion regions configured for the currently-saved
// playmat theme. Used by the dev-only UiInversionEditor to seed its
// initial state when the dev opens the editor (so existing source-
// authored regions are picked up instead of starting from scratch).
export function getCurrentPlaymatInversionRegions() {
  const s = loadSettings();
  const theme = PLAYMAT_THEMES.find((t) => t.id === s.theme);
  return Array.isArray(theme?.uiInversionRegions) ? theme.uiInversionRegions : [];
}

// Slug used by the dev-mode editor when it prints/copies the JSON
// So the dev knows which PLAYMAT_THEMES entry to paste it into.
export function getCurrentPlaymatThemeId() {
  return loadSettings().theme;
}

/**
 * Returns playmat URL(s) for the given settings.
 * - Combined themes: returns a string URL.
 * - Layered themes:  returns { art: string, ui: string }.
 *
 * If the resolved theme is a paid cosmetic and the cached cosmetics
 * snapshot is loaded but doesn't include the entitlement, falls back
 * to vanilla so unauthorized themes can't leak through a stale local
 * pref (e.g. a closed-beta tester who picked a now-paywalled mat
 * before the cosmetics tab existed).
 */
export function getPlaymatUrl(settingsOrId) {
  const settings = typeof settingsOrId === 'object' ? settingsOrId : loadSettings();
  const base = `${getLocalApiOrigin()}/game-assets/playmats`;
  let themeObj = PLAYMAT_THEMES.find((t) => t.id === settings.theme);

  if (themeObj?.cosmeticId) {
    const cache = peekCosmeticsCached();
    if (cache && !cache.owned.includes(themeObj.cosmeticId)) {
      themeObj = PLAYMAT_THEMES[0];
    }
  }

  // Web can't resolve a protected mat's art until the cosmetics
  // catalog (which carries the CDN blobUrl) has loaded; render
  // vanilla until then rather than a broken texture.
  if (isWeb() && themeObj?.protected && !peekProtectedBlobUrl(themeObj.cosmeticId)) {
    themeObj = PLAYMAT_THEMES[0];
  }

  if (themeObj && themeObj.layered) {
    const art = themeObj.protected
      ? protectedPlaymatUrl(themeObj.cosmeticId)
      : `${base}/${themeObj.artFile}`;
    // UI layer is now generated procedurally from this config
    // instead of loading a baked WebP per (lineStyle × numbered ×
    // faded) combo. tableScene.setPlaymat detects the `uiConfig`
    // shape and calls uiOverlayGenerator.generatePlaymatUiTexture.
    //
    // uiColor priority: explicit player override (gridDark
    // true/false) wins; otherwise fall back to the theme's
    // authored default uiColor; otherwise default to white.
    let uiColor;
    // `lightOnly` themes ignore the per-player dark-mode override.
    // the painted background only reads well with the light grid.
    if (themeObj.lightOnly) uiColor = GRID_LIGHT;
    else if (settings.gridDark === true) uiColor = GRID_DARK;
    else if (settings.gridDark === false) uiColor = GRID_LIGHT;
    else uiColor = themeObj.uiColor || GRID_LIGHT;
    return {
      art,
      uiConfig: {
        uiColor,
        gridStyle: settings.lineStyle || 'dashed',
        cellNumbers: settings.fullyNumbered ? 'all' : 'sparse',
        // `fadedLocked` themes always render faded regardless of
        // the player's toggle (which is also hidden in the picker
        // for those themes).
        faded: themeObj.fadedLocked ? true : !!settings.faded,
        thickLines: !!settings.thickLines,
        largeNumbers: !!settings.largeNumbers,
        textureOverlay: themeObj.textureOverlay || null,
        showLifeTracker: settings.showLifeTracker !== false,
        showManaTracker: settings.showManaTracker !== false,
        showAffinityTracker: settings.showAffinityTracker !== false,
        // Licensed mats bake the house + artist seals into the
        // bottom-left corners (Collector Arthouse for P1, the artist
        // for P2). Bundled mats carry no seals.
        cornerLogos: themeObj.protected
          ? { p1: COLLECTOR_ARTHOUSE_SEAL_URL, p2: ARTIST_SEAL_URL }
          : null,
      },
    };
  }
  return `${base}/${buildFilename(settings)}`;
}

/**
 * Returns the vanilla Valkenhall playmat layers for the subsurface
 * crossfade. Used by tableScene.setVanillaPlaymat, which only needs
 * the art layer (the procedural UI overlay is shared between surface
 * and subsurface views). Vanilla is now layered like every other
 * theme. `{art, uiConfig}` instead of a single baked URL.
 */
export function getVanillaPlaymatUrl(settingsOrId) {
  const settings = typeof settingsOrId === 'object' ? settingsOrId : loadSettings();
  return getPlaymatUrl({ ...settings, theme: 'valkenhall-vanilla' });
}

function getPreviewUrl(theme) {
  const t = PLAYMAT_THEMES.find((pt) => pt.id === theme.id) || theme;
  if (t.protected && t.cosmeticId) {
    // Web: load the pre-baked ~480px CDN variant published next to the
    // master by scripts/upload-playmats-to-r2.js. Never the 68 MP master.
    if (isWeb()) return protectedMatVariantUrl(peekProtectedBlobUrl(t.cosmeticId), 'preview');
    const u = protectedPlaymatUrl(t.cosmeticId);
    if (!u) return null;
    // ?size=preview tells the local protected-playmat endpoint to
    // serve a small (~480 px wide) watermarked variant, cached
    // separately from the full art. Picker dropdown + store
    // thumbnails are tiny. The full ~1.5 MB asset is wasted there.
    return `${u}?size=preview`;
  }
  // Bundled mats: prefer the pre-baked preview from
  // `playmats/previews/<file>.webp` (~30 KB) over the full
  // `playmats/art-only/<file>.webp` (1.5–2 MB). The preview path
  // mirrors the art-only basename. See scripts/generate-playmat-previews.js.
  const preview = t.preview;
  if (preview?.startsWith('art-only/')) {
    return `${getLocalApiOrigin()}/game-assets/playmats/previews/${preview.slice('art-only/'.length)}`;
  }
  return `${getLocalApiOrigin()}/game-assets/playmats/${preview}`;
}

const SPINNER_STYLE = {
  width: '2cqw', height: '2cqw',
  border: `3px solid rgba(180,140,60,0.15)`,
  borderTopColor: ACCENT_GOLD,
  borderRadius: '50%',
  animation: 'playmat-spin 0.7s linear infinite',
};

export default class PlaymatPicker extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ...loadSettings(),
      showThemeSelect: false,
      // 'idle' | 'loading' | 'revealing'
      loadPhase: 'idle',
      ownedCosmetics: [],
      // Map<themeId, objectUrl>. Populated by loadPreviewBlobs() in
      // componentDidMount so dropdown thumbnails come from in-memory
      // blobs, not HTTP requests at open time.
      previewBlobUrls: null,
    };
  }

  componentDidMount() {
    // Eagerly fetch every preview as a Blob and turn it into an
    // object URL. Subsequent <img src=…> uses those object URLs.
    // the bitmap is already decoded and in renderer-process memory,
    // so the dropdown shows every thumbnail instantly with zero
    // localhost-server traffic at open time. This sidesteps both
    // HTTP/1.1's 6-connection cap and Chromium's tendency to
    // deprioritise image loads inside `display:none` subtrees.
    this.loadPreviewBlobs();

    // Pull from the shared cache. If the cosmetics store was opened
    // recently this is instant; otherwise the first call refreshes it.
    getCosmeticsCached()
      .then((data) => {
        const owned = data?.owned || [];
        this.setState({ ownedCosmetics: owned });
        // Recover from a stale local pref pointing at a now-paywalled
        // theme: silently revert to vanilla so the next match starts
        // on a theme the player actually owns.
        const current = PLAYMAT_THEMES.find((t) => t.id === this.state.theme);
        if (current && !isThemeOwned(current, owned)) {
          this.applyChange({ theme: 'valkenhall-vanilla' });
        }
      })
      .catch(() => {});
  }

  loadPreviewBlobs = async () => {
    // Seed initial state from the module-level cache so already-loaded
    // previews are available on the very first render after mount.
    const seeded = {};
    for (const t of PLAYMAT_THEMES) {
      if (previewBlobUrlCache.has(t.id)) seeded[t.id] = previewBlobUrlCache.get(t.id);
    }
    if (Object.keys(seeded).length) this.setState({ previewBlobUrls: seeded });

    // Resolve any missing entries in parallel. Each ensurePreviewBlob
    // call is de-duplicated via `inflightBlobLoads`, so two PlaymatPicker
    // mounts during overlapping load cycles share the same fetch.
    await Promise.all(
      PLAYMAT_THEMES.map(async (t) => {
        const obj = await ensurePreviewBlob(t);
        if (this._unmounted || !obj) return;
        this.setState((s) => ({
          previewBlobUrls: { ...(s.previewBlobUrls || {}), [t.id]: obj },
        }));
      })
    );
  };

  componentWillUnmount() {
    this._unmounted = true;
    // Blob URLs live in the module-level cache and stay valid across
    // unmount/remount cycles. Do NOT revoke here. The cache cleans
    // itself up if the renderer process is recreated.
  }

  applyChange = async (updates) => {
    if (this.state.loadPhase !== 'idle') return;
    const next = { ...this.state, ...updates };
    this.setState({ ...updates, showThemeSelect: false, loadPhase: 'loading' });

    const urlOrLayers = getPlaymatUrl(next);
    if (this.props.onChangePlaymat) {
      await this.props.onChangePlaymat(urlOrLayers);
    }

    saveSettings({
      theme: next.theme,
      lineStyle: next.lineStyle,
      fullyNumbered: next.fullyNumbered,
      faded: next.faded,
      gridDark: next.gridDark,
      thickLines: next.thickLines,
      largeNumbers: next.largeNumbers,
      showLifeTracker: next.showLifeTracker,
      showManaTracker: next.showManaTracker,
      showAffinityTracker: next.showAffinityTracker,
    });

    // Brief reveal phase so the overlay fades out smoothly
    this.setState({ loadPhase: 'revealing' });
    setTimeout(() => this.setState({ loadPhase: 'idle' }), 350);
  };

  render() {
    const { onClose, viewScale = 1 } = this.props;
    const { theme, lineStyle, fullyNumbered, faded, gridDark, thickLines, largeNumbers, showThemeSelect, loadPhase, ownedCosmetics, previewBlobUrls } = this.state;
    const previewSrc = (t) => previewBlobUrls?.[t.id] || getPreviewUrl(t);
    // Derive the effective "is the grid dark right now" boolean for
    // toggle-button styling. If the player has set gridDark
    // explicitly, use that; otherwise fall back to a luma check on
    // the theme's default uiColor so the toggle reads as selected on
    // mats that default to a dark grid.
    const themeObjForToggle = PLAYMAT_THEMES.find((t) => t.id === theme);
    const themeIsDark = isHexDark(themeObjForToggle?.uiColor);
    const themeLightOnly = !!themeObjForToggle?.lightOnly;
    const themeFadedLocked = !!themeObjForToggle?.fadedLocked;
    const effectiveGridDark = themeLightOnly ? false : (gridDark === null ? themeIsDark : gridDark);
    const currentTheme = PLAYMAT_THEMES.find((t) => t.id === theme) || PLAYMAT_THEMES[0];
    const locked = loadPhase !== 'idle';
    // No dotted.webp exists (without numbers, without faded), so when
    // on dotted + no faded, All Numbers is always on and can't toggle.
    const numbersForced = lineStyle === 'dotted' && !faded;
    const numbersOn = fullyNumbered || numbersForced;

    return (
      <>
        {/* Spinner keyframes */}
        <style>{`@keyframes playmat-spin { to { transform: rotate(360deg); } }`}</style>

        {/* Loading overlay. Covers the table while textures swap */}
        {locked && (
          <div
            className="fixed inset-0 z-[88] flex items-center justify-center"
            style={{
              background: 'rgba(8, 6, 4, 0.55)',
              backdropFilter: 'blur(2px)',
              opacity: loadPhase === 'revealing' ? 0 : 1,
              transition: 'opacity 0.3s ease-out',
            }}
          >
            {loadPhase === 'loading' && <div style={SPINNER_STYLE} />}
          </div>
        )}

        {/* Backdrop. Dismiss on click (disabled while loading) */}
        <div className="fixed inset-0 z-[89]" onClick={locked ? undefined : onClose} />

        {/* Settings bar */}
        <div
          className="fixed z-[90] left-1/2 -translate-x-1/2"
          style={{ top: '70%', pointerEvents: locked ? 'none' : 'auto', opacity: locked ? 0.5 : 1, transition: 'opacity 0.15s' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="flex items-center gap-3 px-4 py-2.5 rounded-2xl"
            style={{
              background: 'rgba(12, 10, 8, 0.95)',
              border: `1px solid ${GOLD} 0.2)`,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 1px rgba(180,140,60,0.2)',
              backdropFilter: 'blur(12px)',
            }}
          >
            {/* Theme selector button + dropdown */}
            <div className="relative">
              <button
                type="button"
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-md cursor-pointer transition-all hover:bg-white/5"
                onClick={() => this.setState((s) => ({ showThemeSelect: !s.showThemeSelect }))}
              >
                <img
                  src={previewSrc(currentTheme)}
                  alt={currentTheme.name}
                  width={32}
                  height={20}
                  decoding="async"
                  className="w-8 h-5 rounded object-cover"
                  style={{ border: `1px solid ${GOLD} 0.2)` }}
                  draggable={false}
                />
                <span className="text-[0.6875cqw] font-semibold whitespace-nowrap" style={{ color: TEXT_PRIMARY }}>{currentTheme.name}</span>
                <svg viewBox="0 0 10 10" fill="none" style={{ width: '0.625cqw', height: '0.625cqw', color: TEXT_MUTED, transform: showThemeSelect ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                  <path d="M2 6l3-3 3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
              </button>

              {/* Dropdown. Anchored to this button, opens upward.
                  Mounted unconditionally and hidden via OPACITY +
                  pointer-events, NOT `display:none`. Chromium defers
                  image decode for `display:none` subtrees, which
                  causes a visible serial "decode each thumbnail when
                  shown" stutter on first open. Opacity-hiding keeps
                  the element in the render tree so the browser
                  decodes all 8 thumbs at mount, making the actual
                  open a zero-cost CSS opacity flip. */}
              <div
                className="absolute bottom-full left-0 mb-2 w-72 rounded-xl overflow-hidden"
                style={{
                  ...DIALOG_STYLE,
                  boxShadow: '0 -8px 32px rgba(0,0,0,0.6)',
                  opacity: showThemeSelect ? 1 : 0,
                  pointerEvents: showThemeSelect ? 'auto' : 'none',
                  transition: 'opacity 0.12s ease-out',
                }}
                aria-hidden={!showThemeSelect}
              >
                <div className="px-3 pt-3 pb-1 text-[0.625cqw] font-semibold" style={{ color: TEXT_MUTED }}>
                  {t('Choose Play Mat')}
                </div>
                {/* Cap the visible list at 7 mats (each row is ~3.5cqw
                    tall); any beyond that scroll into view. Keeps the
                    dropdown from growing taller than the screen as the
                    catalog of mats expands. */}
                <ScrollArea className="max-h-[24.5cqw]">
                {PLAYMAT_THEMES.map((pt) => {
                  const isActive = theme === pt.id;
                  const owned = isThemeOwned(pt, ownedCosmetics);
                  const locked = !owned;
                  return (
                    <button
                      key={pt.id}
                      type="button"
                      disabled={locked}
                      className="w-full flex items-center gap-3 px-3 py-2 transition-all"
                      style={{
                        background: isActive ? `${GOLD} 0.08)` : 'transparent',
                        opacity: locked ? 0.65 : 1,
                      }}
                      onClick={() => {
                        if (locked) return;
                        if (pt.id !== theme) this.applyChange({ theme: pt.id });
                        else this.setState({ showThemeSelect: false });
                      }}
                    >
                      <div className="relative">
                        <img
                          src={previewSrc(pt)}
                          alt={pt.name}
                          width={64}
                          height={40}
                          decoding="async"
                          className="w-16 h-10 rounded object-cover"
                          style={{ border: `1px solid ${isActive ? ACCENT_GOLD : `${GOLD} 0.15)`}`, filter: locked ? 'grayscale(0.6) brightness(0.7)' : 'none' }}
                          draggable={false}
                        />
                        {locked && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <svg viewBox="0 0 16 16" fill="none" style={{ width: '0.95cqw', height: '0.95cqw', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.9))' }}>
                              <path d="M4 7V5a4 4 0 0 1 8 0v2h1v7H3V7h1Zm2 0h4V5a2 2 0 0 0-4 0v2Z" fill="#e8d5a0" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="text-left min-w-0 flex-1">
                        <div className="text-xs font-semibold" style={{ color: isActive ? ACCENT_GOLD : TEXT_PRIMARY }}>{pt.name}</div>
                        {locked && (
                          <div className="text-[0.55cqw] mt-0.5" style={{ color: TEXT_MUTED }}>
                            {t('Unlock in Cosmetics Store')}
                          </div>
                        )}
                      </div>
                      {isActive && !locked && (
                        <svg style={{ width: '0.875cqw', height: '0.875cqw' }} viewBox="0 0 14 14" fill="none" className="ml-auto shrink-0">
                          <path d="M3 7l3 3 5-6" stroke={ACCENT_GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                  );
                })}
                </ScrollArea>
              </div>
            </div>

            {/* Divider */}
            <div className="w-px h-6" style={{ background: `${GOLD} 0.15)` }} />

            {/* Line style toggle */}
            <div className="flex items-center gap-1">
              {LINE_STYLES.map((ls) => (
                <button
                  key={ls.id}
                  type="button"
                  className="px-2.5 py-1.5 text-[0.625cqw] font-semibold rounded-md cursor-pointer transition-all whitespace-nowrap"
                  style={{
                    background: lineStyle === ls.id ? `${GOLD} 0.12)` : 'transparent',
                    color: lineStyle === ls.id ? ACCENT_GOLD : TEXT_MUTED,
                    border: lineStyle === ls.id ? `1px solid ${GOLD} 0.3)` : '1px solid transparent',
                  }}
                  onClick={() => this.applyChange({ lineStyle: ls.id })}
                >
                  {t(ls.label)}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div className="w-px h-6" style={{ background: `${GOLD} 0.15)` }} />

            {/* All numbers toggle */}
            <button
              type="button"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md cursor-pointer transition-all"
              style={{
                background: numbersOn ? `${GOLD} 0.12)` : 'transparent',
                border: numbersOn ? `1px solid ${GOLD} 0.3)` : '1px solid transparent',
                opacity: numbersForced ? 0.4 : 1,
              }}
              disabled={numbersForced}
              onClick={() => this.applyChange({ fullyNumbered: !fullyNumbered })}
            >
              <div
                className="w-3 h-3 rounded-sm flex items-center justify-center"
                style={{
                  background: numbersOn ? ACCENT_GOLD : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${numbersOn ? ACCENT_GOLD : 'rgba(255,255,255,0.15)'}`,
                }}
              >
                {numbersOn && <svg style={{ width: '0.5cqw', height: '0.5cqw' }} viewBox="0 0 10 10" fill="none"><path d="M2 5l2 2 4-4" stroke="#0e0a06" strokeWidth="1.5" strokeLinecap="round" /></svg>}
              </div>
              <span className="text-[0.625cqw] whitespace-nowrap" style={{ color: numbersOn ? ACCENT_GOLD : TEXT_MUTED }}>{t('All Numbers')}</span>
            </button>

            {/* Faded toggle. Hidden on themes that lock it on
                (e.g. vanilla, where the busy painted border needs
                the UI dialled back to stay legible). */}
            {!themeFadedLocked && (
            <button
              type="button"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md cursor-pointer transition-all"
              style={{
                background: faded ? `${GOLD} 0.12)` : 'transparent',
                border: faded ? `1px solid ${GOLD} 0.3)` : '1px solid transparent',
              }}
              onClick={() => this.applyChange({ faded: !faded })}
            >
              <div
                className="w-3 h-3 rounded-sm flex items-center justify-center"
                style={{
                  background: faded ? ACCENT_GOLD : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${faded ? ACCENT_GOLD : 'rgba(255,255,255,0.15)'}`,
                }}
              >
                {faded && <svg style={{ width: '0.5cqw', height: '0.5cqw' }} viewBox="0 0 10 10" fill="none"><path d="M2 5l2 2 4-4" stroke="#0e0a06" strokeWidth="1.5" strokeLinecap="round" /></svg>}
              </div>
              <span className="text-[0.625cqw] whitespace-nowrap" style={{ color: faded ? ACCENT_GOLD : TEXT_MUTED }}>{t('Faded')}</span>
            </button>
            )}

            {/* Dark grid toggle. Forces the grid lines + cell
                numbers + rune-divider ornament to dark (black, with
                the ornament's gold tones inverted) so the UI reads
                against light playmat artwork. Tapping again returns
                to white (light grid). Stored as a per-player
                override of the theme's default `uiColor`. Hidden on
                `lightOnly` themes where dark mode isn't supported. */}
            {!themeLightOnly && (
            <button
              type="button"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md cursor-pointer transition-all"
              style={{
                background: effectiveGridDark ? `${GOLD} 0.12)` : 'transparent',
                border: effectiveGridDark ? `1px solid ${GOLD} 0.3)` : '1px solid transparent',
              }}
              onClick={() => this.applyChange({ gridDark: !effectiveGridDark })}
            >
              <div
                className="w-3 h-3 rounded-sm flex items-center justify-center"
                style={{
                  background: effectiveGridDark ? ACCENT_GOLD : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${effectiveGridDark ? ACCENT_GOLD : 'rgba(255,255,255,0.15)'}`,
                }}
              >
                {effectiveGridDark && <svg style={{ width: '0.5cqw', height: '0.5cqw' }} viewBox="0 0 10 10" fill="none"><path d="M2 5l2 2 4-4" stroke="#0e0a06" strokeWidth="1.5" strokeLinecap="round" /></svg>}
              </div>
              <span className="text-[0.625cqw] whitespace-nowrap" style={{ color: effectiveGridDark ? ACCENT_GOLD : TEXT_MUTED }}>{t('Dark Grid')}</span>
            </button>
            )}

            {/* Thick lines toggle. Bumps the grid stroke widths
                only. Cell-number font size is its own toggle below
                so players can scale lines and numbers independently. */}
            <button
              type="button"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md cursor-pointer transition-all"
              style={{
                background: thickLines ? `${GOLD} 0.12)` : 'transparent',
                border: thickLines ? `1px solid ${GOLD} 0.3)` : '1px solid transparent',
              }}
              onClick={() => this.applyChange({ thickLines: !thickLines })}
            >
              <div
                className="w-3 h-3 rounded-sm flex items-center justify-center"
                style={{
                  background: thickLines ? ACCENT_GOLD : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${thickLines ? ACCENT_GOLD : 'rgba(255,255,255,0.15)'}`,
                }}
              >
                {thickLines && <svg style={{ width: '0.5cqw', height: '0.5cqw' }} viewBox="0 0 10 10" fill="none"><path d="M2 5l2 2 4-4" stroke="#0e0a06" strokeWidth="1.5" strokeLinecap="round" /></svg>}
              </div>
              <span className="text-[0.625cqw] whitespace-nowrap" style={{ color: thickLines ? ACCENT_GOLD : TEXT_MUTED }}>{t('Thick Lines')}</span>
            </button>

            {/* Large numbers toggle. Bumps the cell-number font
                size + weight only, independent of stroke width. */}
            <button
              type="button"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md cursor-pointer transition-all"
              style={{
                background: largeNumbers ? `${GOLD} 0.12)` : 'transparent',
                border: largeNumbers ? `1px solid ${GOLD} 0.3)` : '1px solid transparent',
              }}
              onClick={() => this.applyChange({ largeNumbers: !largeNumbers })}
            >
              <div
                className="w-3 h-3 rounded-sm flex items-center justify-center"
                style={{
                  background: largeNumbers ? ACCENT_GOLD : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${largeNumbers ? ACCENT_GOLD : 'rgba(255,255,255,0.15)'}`,
                }}
              >
                {largeNumbers && <svg style={{ width: '0.5cqw', height: '0.5cqw' }} viewBox="0 0 10 10" fill="none"><path d="M2 5l2 2 4-4" stroke="#0e0a06" strokeWidth="1.5" strokeLinecap="round" /></svg>}
              </div>
              <span className="text-[0.625cqw] whitespace-nowrap" style={{ color: largeNumbers ? ACCENT_GOLD : TEXT_MUTED }}>{t('Large Numbers')}</span>
            </button>

            {/* Divider */}
            <div className="w-px h-6" style={{ background: `${GOLD} 0.15)` }} />

            {/* Close */}
            <CloseButton onClick={onClose} style={{ width: '1.75cqw', height: '1.75cqw' }} size={12} />
          </div>
        </div>
      </>
    );
  }
}
